// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek
import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { Navbar, Nav } from "react-bootstrap";
import { Dropdown } from "react-bootstrap";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Link, NavLink } from "react-router-dom";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { isSet } from "shared/utils/utils";
import { DataState, MainPanel, INavigationPanelEntry, RootPath, Theme } from "types";
import {
  getSite,
  getPanels,
  getSiteReports,
  getCrossSiteReports,
  hasLocalSettingsPage,
  isOcppFrontend,
  getTheme,
  getInitials,
  getIsSiteOffline,
  getCanModifySiteSettings,
} from "guiutils";
import { ActionType, IAction, raiseAction } from "actions";
import { IState } from "states/states";
import { AppState } from "states/schedulerstates";
import { navEntries } from "./ContentPanel";

import LogoEpm from "icons/logoTextEm";
import IconAlarm from "icons/navAlarm";
import IconConnection from "icons/navConnection";
import IconSettings from "icons/navSettings";
import IconParameters from "icons/navSettings";
import IconReport from "icons/navReport";
import IconNavUser from "icons/navUser";
import IconPassword from "icons/locked";
import IconLogout from "icons/imgDisconnect";

import cssContent from "constants.module.scss";

// uses webpack definitions
declare const IS_CLOUD: boolean;

interface INavigationExtProps {
  showNavigation: boolean;
}

interface INavigationProps {
  appState: AppState;
  userName: string | undefined;
  canModifyUsers: boolean;
  isRfidUser: boolean;
  rfidNumber: string | undefined;
  siteName: string | undefined;
  hasSiteSettings: boolean;
  showSiteParameters: boolean;
  canDisconnectSite: boolean;
  showReports: boolean;
  showLocalSettings: boolean;
  serialNumber: string | undefined;

  isOffline: boolean;
  isBusy: boolean;
  nrPendingAlarms: number;
  panelMap: INavigationPanelEntry[];
  nrPanels: number;
  mainPanel: MainPanel | undefined;
  hasTransaction: boolean;
  canShowAlarms: boolean;

  navigationIsOpen: boolean;
  isDropdownOpen: {
    [panel in MainPanel]?: boolean;
  };

  userId?: number;
}

const mapStateToProps = (state: IState) => {
  const siteId = state.cached.selectedSite;
  const site = getSite(state);
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const isBusy =
    [
      AppState.INIT,
      AppState.LOGGED_IN,
      AppState.SITE_SELECTED,
      AppState.PARAMETER_LOADED,
      AppState.LIVE_DATA_SUBSCRIBED,
    ].includes(state.scheduler.appState) ||
    state.scheduler.trendLoadingState === DataState.SAVE_REQ;
  const { panels, nrPanels } = getPanels(state);

  const isOcppRestricted = isOcppFrontend(state.router.rootPath);
  const isTrendRestricted = state.router.rootPath === RootPath.TREND;
  const isRfidUser = !!state.site.user?.isRfidUser;
  const nrPendingAlarms = state.data.alarms.length;
  const hasLiveData = isSet(state.scheduler.allData.lastData);
  // reports of the current site plus the cross-site ones, of the logged-in user
  const hasReports = [
    ...getSiteReports(state.site.reportList, siteId, isOcppRestricted),
    ...getCrossSiteReports(state.site.reportList, isOcppRestricted),
  ].some((curItem) => curItem.isActive);

  const props: INavigationProps = {
    appState: state.scheduler.appState,
    userName: curUser?.name ?? state.site.user?.realName,
    canModifyUsers: curUser?.accessRights.modifyUsers || false,
    isRfidUser,
    rfidNumber: state.site.user?.secId,
    siteName: site?.name,
    showLocalSettings: hasLocalSettingsPage(state.controller),
    serialNumber: state.controller?.serialNumber,
    isOffline: getIsSiteOffline(state),
    isBusy,
    canShowAlarms:
      !isOcppRestricted &&
      !isTrendRestricted &&
      !isRfidUser &&
      (hasLiveData || nrPendingAlarms > 0),
    nrPendingAlarms,
    panelMap: panels,
    nrPanels,
    mainPanel: state.router.panel,
    hasTransaction: isSet(state.router.transaction),
    navigationIsOpen: state.gui.navigationIsOpen,
    isDropdownOpen: state.gui.isDropdownOpen,
    userId: curUser?.id,
    hasSiteSettings: getCanModifySiteSettings(state),
    showSiteParameters: IS_CLOUD && Boolean(curUser?.accessRights.controllerAdmin),
    // a single site gets auto-selected again right after disconnecting
    canDisconnectSite: state.site.siteList?.length !== 1,
    showReports: hasReports || Boolean(curUser?.accessRights.createReports),
  };
  return props;
};

interface INavigationDispatch {
  onNavigationToggle: () => void;
  onDropdownToggle: (panel: MainPanel) => void;
  onLogout: () => void;
  onChangePasswordClick: () => void;
  onDisconnectSite: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: INavigationDispatch = {
    onNavigationToggle: () => {
      dispatch(raiseAction(ActionType.ACT_NAVIGATION_COLLAPSE));
    },
    onDropdownToggle: (panel: MainPanel) => {
      dispatch(raiseAction(ActionType.ACT_NAVIGATION_DROPDOWN_COLLAPSE, panel));
    },
    onLogout: () => {
      dispatch(raiseAction(ActionType.ACT_LOGOUT_REQ));
    },
    onChangePasswordClick: () => {
      dispatch(raiseAction(ActionType.ACT_CHANGE_PASSWORD));
    },
    onDisconnectSite: () => {
      dispatch(raiseAction(ActionType.ACT_DISCONNECT_SITE));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_copyright: {
    id: "by Energie-Plan + Management GmbH",
  },
  msg_offline: {
    id: "offline",
  },
  msg_localSettings: {
    id: "view controller settings",
  },
});

// messages with placeholder values
const valueMessages = defineMessages<{
  msg_alarmsPending: { count: number };
}>({
  msg_alarmsPending: {
    id: "alarms pending",
    defaultMessage: "{count} alarms pending",
  },
});

type INavigationAllProps = INavigationProps &
  INavigationExtProps &
  INavigationDispatch &
  RoutedProps;

export class NavigationComponent extends React.Component<INavigationAllProps> {
  static contextType = IntlContext;
  declare context: IntlShape;

  private wrapperRef: HTMLDivElement | undefined;
  private readonly PANELS_LIMIT = 6; // above this, we create sub-menus
  private readonly COLLAPSE_PANELS_LIMIT = 3; // above this, we use collapse on small screens

  constructor(props: INavigationAllProps) {
    super(props);
    this.setWrapperRef = this.setWrapperRef.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  public componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside.bind(this));
  }
  public componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside.bind(this));
  }

  private getMainItem(panels: INavigationPanelEntry[]) {
    let mainItem: MainPanel | undefined;
    panels.forEach((curItem) => {
      if (mainItem === undefined) {
        if (curItem.childPanels === undefined) {
          mainItem = curItem.heading;
        } else {
          mainItem = this.getMainItem(curItem.childPanels);
        }
      }
    });
    return mainItem;
  }

  private containsItem(panels: INavigationPanelEntry[], item: MainPanel | undefined): boolean {
    if (!isSet(item)) {
      return false;
    }
    return panels.some((curItem) => {
      if (curItem.heading === item) {
        return true;
      }
      if (curItem.subPanels?.includes(item)) {
        return true;
      }
      if (curItem.childPanels === undefined) {
        // no sub-structure present
        return false;
      }
      return this.containsItem(curItem.childPanels, item);
    });
  }

  public render() {
    const defaultPanel = this.getMainItem(this.props.panelMap);
    const navbarClasses = ["fixed-top"];
    if (
      this.props.appState < AppState.READY ||
      !this.props.showNavigation ||
      this.props.nrPanels <= this.COLLAPSE_PANELS_LIMIT
    ) {
      navbarClasses.push("no-menu");
    }

    return (
      <>
        <div ref={this.setWrapperRef.bind(this)}>
          <Navbar
            expand={
              this.props.showNavigation && this.props.nrPanels > this.COLLAPSE_PANELS_LIMIT ?
                "lg"
              : true
            }
            className={navbarClasses.join(" ")}
            style={
              this.props.navigationIsOpen ?
                { zIndex: cssContent.NAV_FOREGROUND_Z_INDEX }
              : undefined
            }
            variant="light"
          >
            {this.renderLogo(defaultPanel ?? MainPanel.USER)}
            <div id="sitename" className="d-none d-print-inline-block" style={{ marginTop: 5 }}>
              {this.props.siteName}
            </div>
            {this.props.appState >= AppState.READY &&
              (this.props.showNavigation ?
                this.renderNavigationItems()
              : <Nav>{this.getNavItem({ heading: defaultPanel ?? MainPanel.SITES }, 0)}</Nav>)}
            {this.renderButtonsRight()}
          </Navbar>
        </div>
        <div className={this.props.navigationIsOpen ? "modal-backdrop show" : undefined}></div>
      </>
    );
  }

  private renderLogo(defaultPanel: MainPanel | undefined) {
    const isLinked =
      this.props.appState >= AppState.READY || this.props.mainPanel === MainPanel.LOCAL_SETTINGS;
    const logoEl = (
      <LogoEpm
        id="logo"
        className={`${getTheme() === Theme.EPM ? "" : "noEM "}${this.props.isBusy ? "animated" : ""}`}
      />
    );
    return (
      <div
        title={this.context.formatMessage(messages.msg_copyright)}
        className={
          this.props.nrPanels > this.COLLAPSE_PANELS_LIMIT ?
            "navbar-brand"
          : "navbar-brand d-none d-sm-block d-print-block"
        }
      >
        {isLinked && isSet(defaultPanel) ?
          <Link to={`${defaultPanel}`}>{logoEl}</Link>
        : logoEl}
      </div>
    );
  }

  private getNavItems(panelList: INavigationPanelEntry[], level: number) {
    const navItems: JSX.Element[] = [];
    panelList.forEach((curEntry) => {
      if (curEntry.childPanels === undefined) {
        // entry without sub-structure
        navItems.push(this.getNavItem(curEntry, level));
      } else {
        // empty list?
        if (curEntry.childPanels.length === 0) {
          return;
        }
        // single item only or too few panels in total?
        if (curEntry.childPanels.length === 1 || this.props.nrPanels <= this.PANELS_LIMIT) {
          navItems.push(...this.getNavItems(curEntry.childPanels, level));
          return;
        }
        // multiple items; make sub-menu
        const mainPanel = curEntry.heading;
        const isActivePanel = this.containsItem([curEntry], this.props.mainPanel);
        const subItems = this.getNavItems(curEntry.childPanels, level + 1);
        const newItem = (
          <Dropdown
            as="li"
            key={curEntry.heading}
            show={this.props.isDropdownOpen[mainPanel] ? true : false}
            onToggle={() => this.props.onDropdownToggle(mainPanel)}
            className={`nav-item ${isActivePanel ? "active" : ""}`}
          >
            <Dropdown.Toggle as={Nav.Link}>
              {navEntries[curEntry.heading]?.mainIcon ?? navEntries[curEntry.heading]?.icon}{" "}
              {navEntries[curEntry.heading]?.mainLabel ?? navEntries[curEntry.heading]?.label}
            </Dropdown.Toggle>
            <Dropdown.Menu className="static-dropdown">{subItems}</Dropdown.Menu>
          </Dropdown>
        );
        navItems.push(newItem);
      }
    });
    return navItems;
  }

  private renderNavigationItems() {
    return (
      <>
        <Navbar.Collapse in={this.props.navigationIsOpen} className="d-print-none">
          <Nav as="ul" className="navpanels">
            {this.getNavItems(this.props.panelMap, 0)}
          </Nav>
        </Navbar.Collapse>
      </>
    );
  }

  private getNavItem(panel: INavigationPanelEntry, level: number, isDisabled?: boolean) {
    let isActivePanel = this.containsItem([panel], this.props.mainPanel);
    // we need special tweaking of the Active Session panel
    if (this.props.mainPanel === MainPanel.CHARGINGSESSION && this.props.hasTransaction) {
      // if we have a transaction selected, the Active Session shall not be active
      if (panel.heading === MainPanel.CHARGINGSESSION) {
        isActivePanel = false;
      }
      if (panel.heading === MainPanel.CHARGINGSESSIONLIST) {
        isActivePanel = true;
      }
    }
    const classes = ["d-print-none"];
    if (isDisabled) {
      classes.push("disabled");
    }
    if (isActivePanel) {
      classes.push("active");
    }
    if (level === 0) {
      return (
        <Nav.Item key={panel.heading} as="li" className={classes.join(" ")}>
          <Nav.Link as={NavLink} to={`${panel.heading}`} active={isActivePanel}>
            {navEntries[panel.heading]!.icon} {navEntries[panel.heading]!.label}
          </Nav.Link>
        </Nav.Item>
      );
    }
    return (
      <Dropdown.Item
        as="button"
        key={panel.heading}
        id={`navdown_${panel.heading}`}
        className={`level-${level} ${classes.join(" ")}`}
      >
        <NavLink to={`${panel.heading}`}>
          {navEntries[panel.heading]!.label}
          {navEntries[panel.heading]!.icon}
        </NavLink>
      </Dropdown.Item>
    );
  }

  private renderButtonsRight() {
    return (
      <div className="navtopright d-print-none">
        {this.props.isOffline && (
          <span className="nav-offline-badge">
            {this.context.formatMessage(messages.msg_offline)}
          </span>
        )}

        {this.props.canShowAlarms && (
          <NavLink
            className={"nav-item" + (this.props.mainPanel === MainPanel.ALARMS ? " active" : "")}
            to={`${MainPanel.ALARMS}`}
            title={this.context.formatMessage(valueMessages.msg_alarmsPending, {
              count: this.props.nrPendingAlarms,
            })}
          >
            <span className="nav-alarm-wrapper">
              <IconAlarm />
              {this.props.nrPendingAlarms > 0 && (
                <span className="nav-alarm-badge">
                  {this.props.nrPendingAlarms > 99 ? "99+" : this.props.nrPendingAlarms}
                </span>
              )}
            </span>
          </NavLink>
        )}

        {!IS_CLOUD &&
          (this.props.showLocalSettings ?
            this.renderLocalSettings()
          : this.props.appState < AppState.READY &&
            isSet(this.props.serialNumber) && (
              <div>
                <FormattedMessage id="Device" />: {this.props.serialNumber}
              </div>
            ))}

        {this.props.isRfidUser === false &&
          IS_CLOUD &&
          this.props.appState >= AppState.LOGGED_IN &&
          this.renderSite()}

        {IS_CLOUD && this.props.appState >= AppState.LOGGED_IN && this.renderUser()}

        {this.props.showNavigation && this.props.nrPanels > this.COLLAPSE_PANELS_LIMIT && (
          <Navbar.Toggle onClick={this.props.onNavigationToggle} className="d-print-none" />
        )}
      </div>
    );
  }

  private renderSite() {
    if (this.props.appState < AppState.SITE_SELECTED) {
      return (
        <NavLink className="nav-item nav-site-toggle" to={`${MainPanel.SITES}`}>
          <IconConnection />
        </NavLink>
      );
    }
    const siteInitials = getInitials(this.props.siteName);
    return (
      <Dropdown align="end">
        <Dropdown.Toggle
          as="div"
          tabIndex={0}
          className={
            "nav-item nav-site-toggle" +
            ((
              [
                MainPanel.SITES,
                MainPanel.SITE_INFO,
                MainPanel.SITE_SETTINGS,
                MainPanel.SITE_PARAMETERS,
              ].includes(this.props.mainPanel!)
            ) ?
              " active"
            : "")
          }
          title={
            this.props.isOffline ?
              `${this.props.siteName} (${this.context.formatMessage(messages.msg_offline)})`
            : this.props.siteName
          }
        >
          <span className="optional-userinfo">{this.props.siteName}</span>
          <IconConnection className={`${this.props.isOffline ? "offline" : ""}`} />
        </Dropdown.Toggle>
        <Dropdown.Menu className="nav-user-menu">
          <div className="nav-user-header">
            <span className="nav-avatar nav-avatar-site nav-avatar-lg">{siteInitials}</span>
            <div>
              <div className="nav-user-name">{this.props.siteName}</div>
              {this.props.isOffline && (
                <span className="nav-offline-badge">
                  <FormattedMessage id={messages.msg_offline.id} />
                </span>
              )}
            </div>
          </div>
          <Dropdown.Divider />
          <Dropdown.Item as={Link} to={`${MainPanel.SITE_INFO}`}>
            <IconConnection />
            <FormattedMessage id="Site Information" />
          </Dropdown.Item>
          {this.props.hasSiteSettings && (
            <Dropdown.Item as={Link} to={`${MainPanel.SITE_SETTINGS}`}>
              <IconSettings />
              <FormattedMessage id="Site Settings" />
            </Dropdown.Item>
          )}
          {this.props.showSiteParameters && (
            <Dropdown.Item as={Link} to={`${MainPanel.SITE_PARAMETERS}`}>
              <IconParameters />
              <FormattedMessage id="Site Parameters" />
            </Dropdown.Item>
          )}
          {this.props.canDisconnectSite && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item onClick={this.props.onDisconnectSite}>
                <IconLogout />
                <FormattedMessage id="Disconnect" />
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  private renderUser() {
    const userInitials = getInitials(this.props.userName);

    return (
      <Dropdown align="end">
        <Dropdown.Toggle
          as="div"
          tabIndex={0}
          className={
            "nav-item nav-user-toggle" +
            ((
              [MainPanel.USER, MainPanel.USER_SETTINGS, MainPanel.REPORT].includes(
                this.props.mainPanel!,
              )
            ) ?
              " active"
            : "")
          }
          title={this.props.userName}
        >
          <span className="nav-avatar nav-avatar-user">{userInitials}</span>
        </Dropdown.Toggle>
        <Dropdown.Menu className="nav-user-menu">
          <div className="nav-user-header">
            <span className="nav-avatar nav-avatar-user nav-avatar-lg">{userInitials}</span>
            <div>
              <div className="nav-user-name">{this.props.userName}</div>
              <div className="nav-user-role">
                {this.props.canModifyUsers ?
                  <FormattedMessage id="Admin" />
                : this.props.isRfidUser ?
                  this.props.rfidNumber
                : <FormattedMessage id="User" />}
              </div>
            </div>
          </div>
          <Dropdown.Divider />
          <Dropdown.Item as={Link} to={`${MainPanel.USER}`}>
            <IconNavUser />
            <FormattedMessage id="User Information" />
          </Dropdown.Item>
          {/* the settings page shows the own user by default and allows selecting other users */}
          <Dropdown.Item
            as={Link}
            to={
              this.props.isRfidUser ?
                `${MainPanel.USER_SETTINGS}`
              : `${MainPanel.USER_SETTINGS}?user=${this.props.userId}`
            }
          >
            <IconSettings />
            {this.props.canModifyUsers || this.props.isRfidUser ?
              <FormattedMessage id="User Administration" />
            : <FormattedMessage id="User Settings" />}
          </Dropdown.Item>
          {this.props.showReports && (
            <Dropdown.Item as={Link} to={`${MainPanel.REPORT}`}>
              <IconReport />
              <FormattedMessage id="Reports" />
            </Dropdown.Item>
          )}
          <Dropdown.Divider />
          {!this.props.isRfidUser && (
            <Dropdown.Item onClick={this.props.onChangePasswordClick}>
              <IconPassword />
              <FormattedMessage id="Change Password" />
            </Dropdown.Item>
          )}
          <Dropdown.Item onClick={this.props.onLogout}>
            <IconLogout />
            <FormattedMessage id="Sign out" />
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  private renderLocalSettings() {
    return (
      <NavLink
        className={
          "nav-item" + (this.props.mainPanel === MainPanel.LOCAL_SETTINGS ? " active" : "")
        }
        to={`${MainPanel.LOCAL_SETTINGS}`}
        title={this.context.formatMessage(messages.msg_localSettings)}
      >
        <IconSettings />
      </NavLink>
    );
  }

  private setWrapperRef(node: HTMLDivElement) {
    // set the wrapper reference
    this.wrapperRef = node;
  }

  private handleClickOutside(event: MouseEvent) {
    if (
      this.wrapperRef &&
      !this.wrapperRef.contains(event.target as Node) &&
      this.props.navigationIsOpen
    ) {
      // close navigation bar
      this.props.onNavigationToggle();
    }
  }
}

const Navigation = connect(mapStateToProps, mapDispatchToProps)(withRouter(NavigationComponent));

export default Navigation;
